import React, { useState, useEffect, useRef } from 'react';
import { useGridCopyMenu } from './gridCommon';
import DateInput from './DateInput';
import { getMainAxios } from '../utils/util';
import { getApiConfig } from '../config/api';
import { callAxios, callAxiosGet } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { useAuth } from '../context/AuthContext';
import * as signalR from '@microsoft/signalr';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { type PfPos, type PfOrder, type KisPfFill, type KisPfSlice, type WatchlistGroup, type WatchlistItem } from '../types';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface RealTimePrice {
  prodId: string;
  currentPrice: number;
  openPrice: number;
  highPrice: number;
  lowPrice: number;
  volume: number;
  updateTime: string;
}

interface RealTimeDep {
  prodId: string;
  bsopHour: string;
  hourClsCode: string;
  askPrices: (number | null)[];
  bidPrices: (number | null)[];
  askQuantities: (number | null)[];
  bidQuantities: (number | null)[];
  totalAskQuantity: number;
  totalBidQuantity: number;
  antcCnpr: number;
  antcCntgVrss: number;
  antcCntgVrssSign: string;
  antcCntgPrdyCtrt: number;
  antcVol: number;
  standardPrice: number;
}

interface AddOrderParams {
  d: string;
  pfId: number;
  prodId: string;
  ls: number;
  ex: number;
  wei: number;
  qty?: number | null;
  exitAll: boolean;
  limitPrc?: number | null;
  limitIdx?: number | null;
  stopPrc?: number | null;
  hm0: string;
  hm1?: string | null;
  nSlices?: number | null;
  msg?: string | null;
}

const toast = (message: string) => {
  alert(message);
};

const Trading: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const { selectedPfId, canOrder, portfolios } = useAuth();   // canOrder: 주문 권한 없으면 제출 버튼 숨김
  const { msgBox: m, logger } = useMessageState();
  // 선택 전이면 목록 첫 계좌 — 1 로 고정하면 정지된 계좌로 주문을 만든다 (2026-11-03)
  const portfolioId = selectedPfId ?? portfolios[0]?.id ?? 0;

  // 종목 관련 상태
  const [prodIds, setProdIds] = useState<string[]>(['069500', '229200', '114800']);
  const [prodNms, setProdNms] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Array<{prodId: string, prodNm: string, market: string}>>([]);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [selectedStocks, setSelectedStocks] = useState<Array<{prodId: string, prodNm: string, market: string}>>([]);
  const [selectedProdId, setSelectedProdId] = useState<string>('069500');
  const [realTimePrices, setRealTimePrices] = useState<Record<string, RealTimePrice>>({});
  const [realTimeDep, setRealTimeDep] = useState<RealTimeDep | null>(null);
  const [realTimeDeps, setRealTimeDeps] = useState<Record<string, RealTimeDep>>({});
  const [signalRConnected, setSignalRConnected] = useState<boolean>(false);
  const [isRealTimeReceiving, setIsRealTimeReceiving] = useState<boolean>(false);
  const [showAskingPrice, setShowAskingPrice] = useState<boolean>(false);
  const [askingPriceData, setAskingPriceData] = useState<any>(null);
  const [currentPriceData, setCurrentPriceData] = useState<Record<string, { currentPrice: number; standardPrice: number; openPrice: number; highPrice: number; lowPrice: number; volume: number }>>({});
  const [isLoadingCcnl, setIsLoadingCcnl] = useState<boolean>(false);
  const [positions, setPositions] = useState<PfPos[]>([]);
  const [isLoadingPositions, setIsLoadingPositions] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [priceContextMenu, setPriceContextMenu] = useState<{x: number, y: number, price: number, isAsk: boolean} | null>(null);
  const [positionContextMenu, setPositionContextMenu] = useState<{x: number, y: number, prodId: string, price?: number} | null>(null);

  // 관심종목 상태
  const [watchlistGroups, setWatchlistGroups] = useState<WatchlistGroup[]>([]);
  const [selectedWatchlistGroupId, setSelectedWatchlistGroupId] = useState<number | null>(null);
  const [watchlistItems, setWatchlistItems] = useState<WatchlistItem[]>([]);

  // 주문내역/슬라이스/체결내역 상태
  const [orders, setOrders] = useState<PfOrder[]>([]);
  const [slices, setSlices] = useState<KisPfSlice[]>([]);
  const [sliceOrderId, setSliceOrderId] = useState<number | null>(null);
  const [fills, setFills] = useState<KisPfFill[]>([]);
  const [filteredFills, setFilteredFills] = useState<KisPfFill[]>([]);
  const [fillsFiltered, setFillsFiltered] = useState(false); // true = 주문별 필터링 모드

  // 포트폴리오 주문 폼 상태
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    prodId: '',
    prodNm: '',
    isBuy: true,
    wei: 0,
    qty: null as number | null,
    msg: '' as string,
    exitAll: false,
    prcTy: 'Opp' as 'Opp' | 'My' | 'Limit' | 'Stop',
    oppLmtIdx: 1,
    myLmtIdx: 1,
    limitPrc: null as number | null,
    stopPrc: null as number | null,
    isSlice: false,
    hm0: '09:00',
    hm1: '15:20',
    nSlices: null as number | null
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const hubConnectionRef = useRef<signalR.HubConnection | null>(null);
  const axiosInstance = getMainAxios();

  const getSliceStartTime = (dateStr: string) => {
    const today = new Date().toISOString().split('T')[0];
    if (dateStr === today) {
      const now = new Date();
      return `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    }
    return '09:00';
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // 날짜 변경 시 시간분할 시작시간 갱신
      if (field === 'date' && prev.isSlice) {
        next.hm0 = getSliceStartTime(value);
      }
      return next;
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const call = (func: string, params: any, onSuccess: (data: any) => void, onError?: (errMsgs: string[], err: any) => void) => {
    console.log(`API Call: ${func}`, params);
    callAxios({
      axiosInstance,
      m,
      logger,
      url: `/api/v1/orders/${func.toLowerCase()}`,
      params,
      onSuccess,
      onError,
      isGet: false
    });
  };

  // SignalR 연결 설정
  useEffect(() => {
    const apiConfig = getApiConfig();
    const baseURL = apiConfig.BASE_URL;
    const signalRUrl = `${baseURL}/hubs/realtime-price`;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(signalRUrl, { accessTokenFactory: () => localStorage.getItem('fds_token') ?? '' })
      .withAutomaticReconnect([3000, 6000, 9000, 12000, 15000])
      .build();

    connection.on('PriceUpdated', (price: RealTimePrice) => {
      setRealTimePrices(prev => ({ ...prev, [price.prodId]: price }));
    });

    connection.on('DepUpdated', (dep: RealTimeDep) => {
      setRealTimeDeps(prev => ({ ...prev, [dep.prodId]: dep }));
      if (dep.prodId === selectedProdId) {
        setRealTimeDep(dep);
        setShowAskingPrice(true);
      }
    });

    connection.on('PingPong', () => { /* ignore */ });

    connection.start()
      .then(() => {
        setSignalRConnected(true);
        if (isTradingHours()) {
          startRealTimePrice();
        }
      })
      .catch((err) => {
        console.error('[주문] SignalR 연결 실패:', err);
        setSignalRConnected(false);
      });

    connection.onreconnecting(() => setSignalRConnected(false));
    connection.onreconnected(() => {
      setSignalRConnected(true);
      if (isTradingHours()) startRealTimePrice();
    });

    hubConnectionRef.current = connection;
    return () => { connection.stop(); };
  }, []);

  // selectedProdId 변경 시 호가정보 업데이트
  useEffect(() => {
    if (!selectedProdId) return;
    if (isTradingHours() && isRealTimeReceiving) {
      const dep = realTimeDeps[selectedProdId];
      if (dep) { setRealTimeDep(dep); setShowAskingPrice(true); }
    } else {
      fetchAskingPriceForProd(selectedProdId);
    }
  }, [selectedProdId, isRealTimeReceiving, realTimeDeps]);

  // 컴포넌트 마운트 시 포지션 현황, 주식현재가 조회, 관심종목 조회
  useEffect(() => {
    fetchPositions();
    fetchWatchlistGroups();
    if (prodIds.length > 0) fetchCurrentPrices();
    // 주문내역/체결내역 조회
    fetchOrders(portfolioId, selectedDate, selectedDate);
    fetchFills(portfolioId, selectedDate, selectedDate);
  }, [selectedDate]);

  // 관심종목 그룹 변경 시 종목 로드
  useEffect(() => {
    if (selectedWatchlistGroupId) fetchWatchlistItems(selectedWatchlistGroupId);
    else setWatchlistItems([]);
  }, [selectedWatchlistGroupId]);

  // 종목명 조회 (prodIds 변경 시)
  useEffect(() => {
    if (prodIds.length > 0) fetchProdNms(prodIds);
  }, [prodIds]);

  // 검색어 디바운싱
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        handleSearchStocks(searchTerm);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // --- 호가 API 조회 헬퍼 ---
  const padArray = <T,>(arr: T[] | null | undefined, length: number): (T | null)[] => {
    const result: (T | null)[] = Array(length).fill(null);
    if (arr && arr.length > 0) {
      for (let i = 0; i < Math.min(arr.length, length); i++) result[i] = arr[i];
    }
    return result;
  };

  const parseAskingPriceResponse = (data: any, prodId: string): RealTimeDep | null => {
    if (!data?.success || !data?.askingPrice || !data?.expectedPrice) return null;
    const ap = data.askingPrice;
    const ep = data.expectedPrice;
    return {
      prodId: data.prodId || prodId,
      bsopHour: ap.asprAcptHour || '',
      hourClsCode: '',
      askPrices: padArray(ap.askPrices, 10),
      bidPrices: padArray(ap.bidPrices, 10),
      askQuantities: padArray(ap.askQuantities, 10),
      bidQuantities: padArray(ap.bidQuantities, 10),
      totalAskQuantity: ap.totalAskQuantity || 0,
      totalBidQuantity: ap.totalBidQuantity || 0,
      antcCnpr: ep.expectedPrice || 0,
      antcCntgVrss: ep.expectedPriceChange || 0,
      antcCntgVrssSign: ep.expectedPriceChangeSign || '',
      antcCntgPrdyCtrt: ep.expectedPriceChangeRate || 0,
      antcVol: ep.expectedVolume || 0,
      standardPrice: ep.standardPrice || 0
    };
  };

  const fetchAskingPriceForProd = async (prodId: string) => {
    try {
      const response = await axiosInstance.get(`/api/v1/stocks/asking-price-exp-ccn/${prodId}`);
      const depData = parseAskingPriceResponse(response.data, prodId);
      if (depData) {
        setAskingPriceData(response.data);
        setRealTimeDep(depData);
        setShowAskingPrice(true);
      }
    } catch (error) {
      console.error('[주문] 호가/예상체결 조회 실패:', error);
    }
  };

  // --- 포지션 현황 조회 ---
  const fetchPositions = async () => {
    if (isLoadingPositions) return;
    try {
      setIsLoadingPositions(true);
      const date = selectedDate || new Date().toISOString().split('T')[0];
      const response = await axiosInstance.get('/api/v1/portfolio/positions', {
        params: { id: portfolioId, d: date }
      });
      let positionsData: any[] = [];
      if (Array.isArray(response.data)) {
        positionsData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data.data)) positionsData = response.data.data;
        else if (Array.isArray(response.data.positions)) positionsData = response.data.positions;
        else if (Array.isArray(response.data.pos)) positionsData = response.data.pos;
      }
      setPositions(positionsData as PfPos[]);
    } catch (error: any) {
      console.error('[주문] 포지션 현황 조회 실패:', error);
      setPositions([]);
    } finally {
      setIsLoadingPositions(false);
    }
  };

  // --- 종목명 조회 ---
  const fetchProdNms = async (targetProdIds: string[]) => {
    if (!targetProdIds || targetProdIds.length === 0) return;
    try {
      const qs = targetProdIds.map(id => `prodIds=${encodeURIComponent(id)}`).join('&');
      console.log('[주문] fetchProdNms 호출:', targetProdIds, 'URL:', `/api/v1/stocks/prod-nms?${qs}`);
      const response = await axiosInstance.get(`/api/v1/stocks/prod-nms?${qs}`);
      console.log('[주문] fetchProdNms 응답:', response.data);
      if (response.data?.success && response.data?.data) {
        const newProdNms = response.data.data;
        if (typeof newProdNms === 'object' && newProdNms !== null) {
          setProdNms(prev => ({ ...prev, ...newProdNms }));
        }
      }
    } catch (error: any) {
      console.error('[주문] 종목명 조회 실패:', error?.response?.status, error?.response?.data, error);
    }
  };

  // --- 종목 검색 ---
  const handleSearchStocks = async (term: string): Promise<Array<{prodId: string, prodNm: string, market: string}>> => {
    if (!term.trim() || term.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return [];
    }
    try {
      const response = await axiosInstance.get('/api/v1/stocks/search', {
        params: { term: term.trim(), limit: 20 }
      });
      if (response.data?.success && response.data?.data) {
        setSearchResults(response.data.data);
        setShowSearchResults(true);
        return response.data.data;
      } else {
        setSearchResults([]);
        setShowSearchResults(true);
        return [];
      }
    } catch (error: any) {
      console.error('[주문] 종목 검색 실패:', error);
      setSearchResults([]);
      setShowSearchResults(false);
      return [];
    }
  };

  const handleSearchButtonClick = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    const inputValue = searchTerm.trim();
    if (!inputValue || inputValue.length < 2) {
      alert('검색어를 2자 이상 입력해주세요.');
      return;
    }
    const results = await handleSearchStocks(inputValue);
    if (!results || results.length === 0) {
      setShowSearchResults(true);
      alert('검색 결과가 없습니다.');
    }
  };

  // --- 종목 추가/삭제 ---
  const handleAddProdId = async (prodId?: string) => {
    const targetProdId = prodId || searchTerm.trim();
    if (!targetProdId) { alert('종목코드를 입력하세요.'); return; }
    if (prodIds.includes(targetProdId)) { alert('이미 추가된 종목입니다.'); return; }
    await fetchProdNms([targetProdId]);
    setProdIds(prev => [...prev, targetProdId]);
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const handleToggleStock = (stock: {prodId: string, prodNm: string, market: string}) => {
    setSelectedStocks(prev => {
      const isSelected = prev.some(s => s.prodId === stock.prodId);
      return isSelected ? prev.filter(s => s.prodId !== stock.prodId) : [...prev, stock];
    });
  };

  const handleAddSelectedStocks = async () => {
    if (selectedStocks.length === 0) { alert('추가할 종목을 선택해주세요.'); return; }
    const newProdIds = selectedStocks.map(s => s.prodId).filter(id => !prodIds.includes(id));
    if (newProdIds.length === 0) { alert('모든 선택한 종목이 이미 추가되어 있습니다.'); setSelectedStocks([]); return; }
    await fetchProdNms(newProdIds);
    setProdIds(prev => [...prev, ...newProdIds]);
    setSelectedStocks([]);
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const handleRemoveProdId = (prodId: string) => {
    if (prodIds.length <= 1) { alert('최소 1개 종목은 유지해야 합니다.'); return; }
    setProdIds(prev => prev.filter(id => id !== prodId));
    if (selectedProdId === prodId) {
      setSelectedProdId(prodIds[0] === prodId ? prodIds[1] : prodIds[0]);
    }
  };

  // --- 장 운영 시간 ---
  const isTradingHours = (): boolean => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    return currentTime >= 8 * 60 + 30 && currentTime <= 15 * 60 + 35;
  };

  // --- 실시간 시작/중지 ---
  const startRealTimePrice = async () => {
    if (!isTradingHours()) {
      alert('실시간 데이터는 장 운영 시간(08:30 ~ 15:35)에만 이용 가능합니다.');
      return;
    }
    try {
      await axiosInstance.post('/api/v1/stocks/realtime-price/start', { prodIds });
      setIsRealTimeReceiving(true);
    } catch (error: any) {
      console.error('[주문] 실시간 가격 수신 시작 실패:', error);
    }
  };

  const stopRealTimePrice = async () => {
    try {
      await axiosInstance.post('/api/v1/stocks/realtime-price/stop');
      setIsRealTimeReceiving(false);
    } catch (error: any) {
      console.error('[주문] 실시간 가격 수신 중지 실패:', error);
      alert(error?.response?.data?.message || '실시간 가격 수신 중지 실패');
    }
  };

  // --- 주식현재가 조회 ---
  const fetchCurrentPrices = async () => {
    if (isLoadingCcnl) return;
    try {
      setIsLoadingCcnl(true);
      const promises = prodIds.map(async (prodId, index) => {
        try {
          if (index > 0) await new Promise(resolve => setTimeout(resolve, 1000));
          const currentPriceResponse = await axiosInstance.get(`/api/v1/stocks/current-price/${prodId}`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          if (!currentPriceResponse?.data || !currentPriceResponse.data.success) return null;
          const currentPrice = currentPriceResponse.data.currentPrice;
          if (currentPrice === undefined || currentPrice === null) return null;
          const prodNm = currentPriceResponse.data.prodNm || '';
          const standardPrice = currentPriceResponse.data.standardPrice ?? 0;
          const openPrice = currentPriceResponse.data.openPrice ?? 0;
          const highPrice = currentPriceResponse.data.highPrice ?? 0;
          const lowPrice = currentPriceResponse.data.lowPrice ?? 0;
          const volume = currentPriceResponse.data.volume ?? 0;
          if (prodNm) setProdNms(prev => ({ ...prev, [prodId]: prodNm }));
          setCurrentPriceData(prev => ({ ...prev, [prodId]: { currentPrice, standardPrice, openPrice, highPrice, lowPrice, volume } }));
          const updatedPrice: RealTimePrice = {
            prodId, currentPrice,
            openPrice: openPrice || currentPrice,
            highPrice: highPrice || currentPrice,
            lowPrice: lowPrice || currentPrice,
            volume,
            updateTime: currentPriceResponse.data.timestamp || new Date().toISOString()
          };
          return { prodId, price: updatedPrice, prodNm };
        } catch { return null; }
      });
      const results = await Promise.all(promises);
      const successfulResults = results.filter(r => r !== null);
      if (successfulResults.length > 0) {
        setRealTimePrices(prev => {
          const updated = { ...prev };
          successfulResults.forEach(result => { if (result) updated[result.prodId] = result.price; });
          return updated;
        });
      }
    } catch (error: any) {
      console.error('[주문] 주식현재가 조회 실패:', error);
    } finally {
      setIsLoadingCcnl(false);
    }
  };

  // --- 관심종목 그룹/종목 조회 ---
  const fetchWatchlistGroups = async () => {
    try {
      const res = await axiosInstance.get('/api/v1/watchlist/groups');
      const grps = res.data || [];
      setWatchlistGroups(grps);
      if (grps.length > 0 && !selectedWatchlistGroupId) {
        setSelectedWatchlistGroupId(grps[0].id);
      }
    } catch (err) {
      console.error('[주문] 관심종목 그룹 조회 실패:', err);
    }
  };

  const fetchWatchlistItems = async (groupId: number) => {
    try {
      const res = await axiosInstance.get(`/api/v1/watchlist/groups/${groupId}/items`);
      const itemsData: WatchlistItem[] = res.data || [];
      setWatchlistItems(itemsData);
      // 종목 목록 갱신
      const wlProdIds = itemsData.map(i => i.prodId);
      if (wlProdIds.length > 0) {
        // 종목명 조회 (await로 완료 보장)
        await fetchProdNms(wlProdIds);
        // prodIds 갱신 (SignalR 구독 및 현재가 조회용)
        setProdIds(prev => {
          const merged = [...new Set([...prev, ...wlProdIds])];
          return merged;
        });
      }
    } catch (err) {
      console.error('[주문] 관심종목 종목 조회 실패:', err);
    }
  };

  // --- 주문내역/슬라이스/체결내역 조회 ---
  const fetchOrders = (pfId: number, d0: string, d1: string) => {
    callAxiosGet({
      axiosInstance,
      m,
      logger,
      url: '/api/v1/portfolio/orders',
      params: { id: pfId, d0, d1 },
      onSuccess: (data) => {
        setOrders(data as PfOrder[] || []);
      },
      onError: () => { setOrders([]); },
    });
  };

  const fetchFills = (pfId: number, startDate?: string, endDate?: string, clearFilter?: boolean) => {
    const today = new Date().toISOString().split('T')[0];
    callAxiosGet({
      axiosInstance,
      m,
      logger,
      url: '/api/v1/pfbs/fills',
      params: { pfId, startDate: startDate || today, endDate: endDate || today },
      onSuccess: (data) => {
        const fillData = Array.isArray(data) ? data as KisPfFill[] : [];
        setFills(fillData);
        if (clearFilter) {
          setFillsFiltered(false);
          setFilteredFills([]);
        }
      },
      onError: () => { setFills([]); },
    });
  };

  const fetchSlices = (ordId: number) => {
    callAxiosGet({
      axiosInstance,
      m,
      logger,
      url: '/api/v1/pfbs/slices',
      params: { ordId },
      onSuccess: (data) => {
        const sliceData = Array.isArray(data) ? data as KisPfSlice[] : [];
        setSlices(sliceData);
        setSliceOrderId(ordId);
        // 슬라이스의 ordNo 목록으로 체결내역 필터링
        const ordNos = new Set(sliceData.map(s => s.ordNo).filter((n): n is number => n != null && n > 0));
        if (ordNos.size > 0) {
          setFilteredFills(fills.filter(f => ordNos.has(f.ordNo)));
          setFillsFiltered(true);
        } else {
          setFilteredFills([]);
          setFillsFiltered(true);
        }
      },
      onError: () => { setSlices([]); setSliceOrderId(ordId); setFilteredFills([]); setFillsFiltered(true); },
    });
  };

  // --- 호가 클릭 → 주문폼 연동 ---
  const handleHogaClick = (price: number, isAsk: boolean) => {
    const prodId = selectedProdId;
    const prodNm = prodNms[prodId] || '';
    setFormData(prev => ({
      ...prev,
      prodId,
      prodNm,
      isBuy: !isAsk, // 매도호가 클릭 → 매수, 매수호가 클릭 → 매도
      prcTy: 'Limit',
      limitPrc: price
    }));
  };

  // --- 포트폴리오 주문 제출 ---
  const handleOrderSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: {[key: string]: string} = {};
    if (!formData.prodId) newErrors.prodId = '종목을 선택하세요';
    if (!formData.exitAll && formData.wei <= 0 && (!formData.qty || formData.qty <= 0))
      newErrors.wei = '비중 또는 수량을 입력하세요';
    if (formData.prcTy === 'Opp' && (!formData.oppLmtIdx || formData.oppLmtIdx < 1 || formData.oppLmtIdx > 10))
      newErrors.oppLmtIdx = '상대호가는 1-10 사이여야 합니다';
    if (formData.prcTy === 'My' && (!formData.myLmtIdx || formData.myLmtIdx < 1 || formData.myLmtIdx > 10))
      newErrors.myLmtIdx = '자기호가는 1-10 사이여야 합니다';
    if (formData.prcTy === 'Limit' && !formData.limitPrc)
      newErrors.limitPrc = '지정가를 입력하세요';
    if (formData.prcTy === 'Stop' && !formData.stopPrc)
      newErrors.stopPrc = 'STOP 가격을 입력하세요';
    if (formData.isSlice && formData.hm0 && formData.hm1 && formData.hm1 <= formData.hm0)
      newErrors.hm1 = '종료시간이 시작시간보다 늦어야 합니다';

    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    const addParams: AddOrderParams = {
      d: formData.date,
      pfId: portfolioId,
      prodId: formData.prodId,
      ls: 1,
      ex: formData.isBuy ? 1 : -1,
      wei: formData.wei / 100,
      qty: formData.qty && formData.qty > 0 ? formData.qty : null,
      exitAll: formData.exitAll,
      limitPrc: formData.prcTy === 'Limit' ? formData.limitPrc : null,
      limitIdx: formData.prcTy === 'Opp' ? formData.oppLmtIdx :
                formData.prcTy === 'My' ? -(formData.myLmtIdx || 0) : null,
      stopPrc: formData.prcTy === 'Stop' ? formData.stopPrc : null,
      hm0: formData.hm0,
      hm1: formData.isSlice ? formData.hm1 : null,
      nSlices: formData.isSlice ? formData.nSlices : null,
      msg: formData.msg || null,
    };

    call('add', addParams, () => {
      toast('주문 추가 완료');
      // 폼 초기화
      setFormData(prev => ({
        ...prev,
        prodId: '',
        prodNm: '',
        isBuy: true,
        wei: 0,
        qty: null,
        msg: '',
        exitAll: false,
        prcTy: 'Opp',
        limitPrc: null,
        stopPrc: null,
      }));
      setErrors({});
    });
  };

  // --- 가격 색상 헬퍼 ---
  const getPriceColor = (price: number | null | undefined, standardPrice: number): string => {
    if (!price) return '#6b7280';
    const priceNum = typeof price === 'number' ? price : parseFloat(String(price));
    const stdNum = typeof standardPrice === 'number' ? standardPrice : parseFloat(String(standardPrice));
    if (stdNum > 0) {
      if (Math.abs(priceNum - stdNum) < 0.01) return '#000000';
      if (priceNum > stdNum) return '#ef4444';
      return '#3b82f6';
    }
    return '#3b82f6';
  };

  const isStandardPrice = (price: number | null | undefined, standardPrice: number): boolean => {
    if (!price) return false;
    const priceNum = typeof price === 'number' ? price : parseFloat(String(price));
    const stdNum = typeof standardPrice === 'number' ? standardPrice : parseFloat(String(standardPrice));
    return stdNum > 0 && Math.abs(priceNum - stdNum) < 0.01;
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr || timeStr.length !== 6) return timeStr;
    return `${timeStr.substring(0, 2)}:${timeStr.substring(2, 4)}:${timeStr.substring(4, 6)}`;
  };

  // --- AG Grid Column Definitions ---
  const defaultColDef: ColDef = { sortable: true, filter: true, resizable: true, flex: 0 };

  const stateMap: Record<number, { label: string; color: string }> = {
    [-10]: { label: '삭제', color: '#9ca3af' },
    [-9]: { label: '거절', color: '#ef4444' },
    0: { label: '미접수', color: '#f59e0b' },
    10: { label: '접수', color: '#3b82f6' },
    20: { label: '작동', color: '#22c55e' },
    30: { label: '완료', color: '#6b7280' },
  };

  const orderColumnDefs: ColDef<PfOrder>[] = [
    { headerName: 'ID', field: 'id', width: 70, cellStyle: { textAlign: 'right' } },
    { headerName: '날짜', field: 'd', width: 100 },
    { headerName: '종목', field: 'prodId', width: 80 },
    { headerName: '종목명', field: 'prodNm', width: 120 },
    {
      headerName: 'LS', field: 'ls', width: 60,
      cellStyle: (p) => ({ textAlign: 'center', fontWeight: 'bold', color: p.value === 1 ? '#22c55e' : '#ef4444' }),
      valueFormatter: (p) => p.value === 1 ? '롱' : '숏',
    },
    {
      headerName: 'EX', field: 'ex', width: 60,
      cellStyle: (p) => ({ textAlign: 'center', fontWeight: 'bold', color: p.value === 1 ? '#3b82f6' : '#f59e0b' }),
      valueFormatter: (p) => p.value === 1 ? '진입' : '청산',
    },
    {
      headerName: '비중%', field: 'wei', width: 80, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? (p.value * 100).toFixed(2) : '',
    },
    {
      headerName: '지정가', field: 'limitPrc', width: 90, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '',
    },
    {
      headerName: '시작', width: 70,
      valueGetter: (p) => p.data?.hm0 || (p.data?.t0 ? new Date(p.data.t0).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : ''),
    },
    {
      headerName: '종료', width: 70,
      valueGetter: (p) => p.data?.hm1 || (p.data?.t1 ? new Date(p.data.t1).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : ''),
    },
    {
      headerName: '상태', field: 'state', width: 70,
      cellStyle: (p) => ({ textAlign: 'center', fontWeight: 'bold', color: stateMap[p.value]?.color || '#374151' }),
      valueFormatter: (p) => stateMap[p.value]?.label || String(p.value),
    },
    {
      headerName: '주문수량', field: 'qty2ord', width: 90, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '',
    },
    {
      headerName: '체결수량', field: 'cumQty', width: 90, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '',
    },
    {
      headerName: '체결단가', field: 'avgPx', width: 90, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '',
    },
    { headerName: '코멘트', field: 'msg', width: 150 },
    { headerName: '오류', field: 'errMsg', width: 200 },
    {
      headerName: '전량', field: 'exitAll', width: 55,
      cellStyle: { textAlign: 'center', fontWeight: 'bold', color: '#ef4444' },
      valueFormatter: (p) => p.value ? 'T' : '',
    },
  ];

  const sliceColumnDefs: ColDef<KisPfSlice>[] = [
    { headerName: 'ID', field: 'id', width: 70, cellStyle: { textAlign: 'right' } },
    { headerName: '시각', field: 't', width: 140 },
    { headerName: '주문수량', field: 'qty2ord', width: 90, cellStyle: { textAlign: 'right' }, valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '' },
    { headerName: '체결수량', field: 'fillCumQty', width: 90, cellStyle: { textAlign: 'right', fontWeight: 'bold' }, valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '' },
    { headerName: '지정가', field: 'limitPrc', width: 100, cellStyle: { textAlign: 'right' }, valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '' },
    { headerName: '주문번호', field: 'ordNo', width: 100, cellStyle: { textAlign: 'right' } },
    { headerName: '접수시각', field: 'ordAcceptT', width: 90 },
    { headerName: 'KRX번호', field: 'krxOrgNo', width: 100 },
    { headerName: '취소시각', field: 'cnclT', width: 140, cellStyle: (p) => ({ color: p.value ? '#ef4444' : undefined }) },
    {
      headerName: '완료', field: 'done', width: 60,
      cellStyle: (p) => ({ textAlign: 'center', fontWeight: 'bold', color: p.value ? '#22c55e' : '#f59e0b' }),
      valueFormatter: (p) => p.value ? 'Y' : 'N',
    },
    { headerName: '오류', field: 'errMsg', width: 200 },
  ];

  const fillColumnDefs: ColDef<KisPfFill>[] = [
    { headerName: '날짜', field: 'd', width: 100 },
    { headerName: '주문번호', field: 'ordNo', width: 100, cellStyle: { textAlign: 'right' } },
    { headerName: '원주문번호', field: 'orgOrdNo', width: 100, cellStyle: { textAlign: 'right' } },
    { headerName: '주문구분', field: 'ordTyNm', width: 100 },
    {
      headerName: '매매', field: 'lsNm', width: 70,
      cellStyle: (p) => ({ textAlign: 'center', fontWeight: 'bold', color: p.value === '매수' ? '#ef4444' : '#22c55e' }),
    },
    { headerName: '종목코드', field: 'prodId', width: 90 },
    { headerName: '종목명', field: 'prodNm', width: 120 },
    { headerName: '주문수량', field: 'ordQty', width: 90, cellStyle: { textAlign: 'right' }, valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '' },
    { headerName: '주문가', field: 'ordPrc', width: 90, cellStyle: { textAlign: 'right' }, valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '' },
    { headerName: '체결수량', field: 'totFillQty', width: 90, cellStyle: { textAlign: 'right', fontWeight: 'bold' }, valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '' },
    {
      headerName: '미체결', field: 'rmnQty', width: 80,
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 'bold', color: p.value > 0 ? '#f59e0b' : '#22c55e' }),
      valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '',
    },
    { headerName: '체결단가', field: 'avgPrc', width: 90, cellStyle: { textAlign: 'right' }, valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '' },
    { headerName: '체결금액', field: 'totFillAmt', width: 110, cellStyle: { textAlign: 'right', fontWeight: 'bold' }, valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '' },
    {
      headerName: '취소', field: 'cnclYn', width: 60,
      cellStyle: (p) => ({ textAlign: 'center', fontWeight: 'bold', color: p.value === 'Y' ? '#ef4444' : '#22c55e' }),
    },
  ];

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 헤더: 날짜 선택 및 종목 검색, 실시간 버튼 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="trading-date-picker" style={{ fontWeight: 'bold', color: '#1e40af', fontSize: '12px', whiteSpace: 'nowrap' }}>조회일자:</label>
            <DateInput
              id="trading-date-picker"
              value={selectedDate}
              onChange={(v) => setSelectedDate(v)}
              style={{ border: '2px solid #3b82f6' }}
            />
          </div>
          {/* 관심종목 그룹 선택 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#1e40af', whiteSpace: 'nowrap' }}>관심종목:</label>
            <select
              value={selectedWatchlistGroupId ?? ''}
              onChange={(e) => setSelectedWatchlistGroupId(e.target.value ? Number(e.target.value) : null)}
              style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '12px' }}
            >
              <option value="">선택</option>
              {watchlistGroups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* 종목 검색 */}
            <div style={{ position: 'relative', display: 'flex', gap: '4px', alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => { if (searchResults.length > 0) setShowSearchResults(true); }}
                  onBlur={(e) => {
                    const relatedTarget = e.relatedTarget as HTMLElement;
                    if (!relatedTarget || !relatedTarget.closest('.search-results-dropdown')) {
                      setTimeout(() => setShowSearchResults(false), 300);
                    }
                  }}
                  placeholder="종목코드/종목명 검색"
                  style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '12px', width: '180px' }}
                />
                {showSearchResults && (
                  <div
                    className="search-results-dropdown"
                    onMouseDown={(e) => e.preventDefault()}
                    style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
                      backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '4px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', maxHeight: '300px', overflowY: 'auto', zIndex: 1000
                    }}
                  >
                    {searchResults.length > 0 ? searchResults.map((result, idx) => {
                      const isChecked = selectedStocks.some(s => s.prodId === result.prodId);
                      const isAlreadyAdded = prodIds.includes(result.prodId);
                      return (
                        <div
                          key={idx}
                          onClick={(e) => { if (!isAlreadyAdded) { e.stopPropagation(); handleToggleStock(result); } }}
                          style={{
                            padding: '8px 12px', cursor: isAlreadyAdded ? 'default' : 'pointer',
                            borderBottom: idx < searchResults.length - 1 ? '1px solid #e5e7eb' : 'none',
                            backgroundColor: isAlreadyAdded ? '#f3f4f6' : (isChecked ? '#eff6ff' : 'white')
                          }}
                          onMouseEnter={(e) => { if (!isAlreadyAdded) e.currentTarget.style.backgroundColor = isChecked ? '#dbeafe' : '#f9fafb'; }}
                          onMouseLeave={(e) => { if (!isAlreadyAdded) e.currentTarget.style.backgroundColor = isChecked ? '#eff6ff' : 'white'; }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="checkbox" checked={isChecked} disabled={isAlreadyAdded}
                              onChange={() => { if (!isAlreadyAdded) handleToggleStock(result); }}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '16px', height: '16px', cursor: isAlreadyAdded ? 'not-allowed' : 'pointer' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '12px', fontWeight: '600', color: isAlreadyAdded ? '#9ca3af' : '#1e40af' }}>{result.prodNm}</div>
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{result.prodId} ({result.market})</div>
                            </div>
                            {isAlreadyAdded && <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: '500' }}>추가됨</span>}
                          </div>
                        </div>
                      );
                    }) : (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>검색 결과가 없습니다.</div>
                    )}
                  </div>
                )}
              </div>
              <button type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSearchButtonClick(e); }}
                onMouseDown={(e) => e.preventDefault()}
                disabled={!searchTerm.trim() || searchTerm.trim().length < 2}
                style={{
                  padding: '4px 12px',
                  backgroundColor: (searchTerm.trim() && searchTerm.trim().length >= 2) ? '#3b82f6' : '#9ca3af',
                  color: 'white', border: 'none', borderRadius: '3px', fontSize: '12px', fontWeight: '500',
                  cursor: (searchTerm.trim() && searchTerm.trim().length >= 2) ? 'pointer' : 'not-allowed'
                }}
              >검색</button>
            </div>
          </div>
          {/* 실시간 시작/중지 */}
          {isRealTimeReceiving ? (
            <button onClick={stopRealTimePrice}
              style={{ padding: '5px 12px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '3px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
              실시간 중지
            </button>
          ) : (
            <button onClick={startRealTimePrice} disabled={!isTradingHours()}
              style={{
                padding: '5px 12px', backgroundColor: isTradingHours() ? '#22c55e' : '#9ca3af',
                color: 'white', border: 'none', borderRadius: '3px', fontSize: '12px', fontWeight: '500',
                cursor: isTradingHours() ? 'pointer' : 'not-allowed', opacity: isTradingHours() ? 1 : 0.6
              }}>
              실시간 시작 {!isTradingHours() && '(08:30 ~ 15:35)'}
            </button>
          )}
          {/* SignalR 연결 상태 */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px',
            backgroundColor: signalRConnected ? '#22c55e' : '#ef4444', borderRadius: '4px', fontSize: '11px', color: 'white', fontWeight: '500'
          }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'white' }} />
            {signalRConnected ? '연결됨' : '끊김'}
          </div>
        </div>
        {/* 선택된 종목 목록 */}
        {selectedStocks.length > 0 && (
          <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>선택된 종목 ({selectedStocks.length}개)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
              {selectedStocks.map((stock) => (
                <div key={stock.prodId} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 6px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }}>
                  <span style={{ color: '#1e40af', fontWeight: '500' }}>{stock.prodNm}</span>
                  <span style={{ color: '#6b7280' }}>({stock.prodId})</span>
                  <button onClick={() => handleToggleStock(stock)} style={{ marginLeft: '4px', padding: '0 4px', backgroundColor: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>×</button>
                </div>
              ))}
            </div>
            <button onClick={handleAddSelectedStocks}
              style={{ padding: '4px 12px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '3px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', alignSelf: 'flex-start' }}>
              선택한 종목 추가 ({selectedStocks.length}개)
            </button>
          </div>
        )}
      </div>

      {/* 현재가 테이블 (관심종목 기반) */}
      {watchlistItems.length > 0 && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', margin: '0 0 8px 0' }}>
            현재가 — {watchlistGroups.find(g => g.id === selectedWatchlistGroupId)?.name || ''}
          </h3>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '600', fontSize: '11px' }}>종목코드</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '600', fontSize: '11px' }}>종목명</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '600', fontSize: '11px' }}>현재가</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '600', fontSize: '11px' }}>기준가</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '600', fontSize: '11px' }}>등락률</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '600', fontSize: '11px' }}>거래량</th>
                </tr>
              </thead>
              <tbody>
                {watchlistItems.map((item) => {
                  const rtPrice = realTimePrices[item.prodId];
                  const cpd = currentPriceData[item.prodId];
                  const currentPrice = rtPrice?.currentPrice || cpd?.currentPrice || 0;
                  const standardPrice = cpd?.standardPrice || 0;
                  const volume = rtPrice?.volume || cpd?.volume || 0;
                  const changeRate = standardPrice > 0 ? ((currentPrice - standardPrice) / standardPrice * 100) : 0;
                  const isSelected = selectedProdId === item.prodId;

                  return (
                    <tr key={item.id}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: isSelected ? '#eff6ff' : 'white',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setSelectedProdId(item.prodId);
                        setFormData(prev => ({ ...prev, prodId: item.prodId, prodNm: prodNms[item.prodId] || '' }));
                        if (isTradingHours() && isRealTimeReceiving) {
                          const dep = realTimeDeps[item.prodId];
                          if (dep) { setRealTimeDep(dep); setShowAskingPrice(true); }
                        } else {
                          fetchAskingPriceForProd(item.prodId);
                        }
                      }}
                    >
                      <td style={{ padding: '4px 8px', textAlign: 'center', fontSize: '11px', fontWeight: isSelected ? '600' : '400', color: isSelected ? '#1e40af' : '#374151' }}>
                        {item.prodId}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'left', fontSize: '11px' }}>
                        {prodNms[item.prodId] || '-'}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: getPriceColor(currentPrice, standardPrice) }}>
                        {currentPrice > 0 ? currentPrice.toLocaleString() : '-'}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '11px', color: '#6b7280' }}>
                        {standardPrice > 0 ? standardPrice.toLocaleString() : '-'}
                      </td>
                      <td style={{
                        padding: '4px 8px', textAlign: 'right', fontSize: '11px', fontWeight: '600',
                        color: changeRate > 0 ? '#ef4444' : changeRate < 0 ? '#3b82f6' : '#374151'
                      }}>
                        {standardPrice > 0 ? `${changeRate >= 0 ? '+' : ''}${changeRate.toFixed(2)}%` : '-'}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '11px', color: '#6b7280' }}>
                        {volume > 0 ? volume.toLocaleString() : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 메인 2컬럼: 호가 (좌) + 주문폼 (우) */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: '16px', marginBottom: '20px' }}>

        {/* 좌측: 호가정보 */}
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', margin: 0 }}>호가정보</h3>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {/* 종목 선택 드롭다운 */}
              <select
                value={selectedProdId}
                onChange={(e) => setSelectedProdId(e.target.value)}
                style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }}
              >
                {prodIds.map(id => (
                  <option key={id} value={id}>{id} {prodNms[id] ? `(${prodNms[id]})` : ''}</option>
                ))}
              </select>
              <button
                onClick={() => fetchAskingPriceForProd(selectedProdId)}
                style={{ padding: '4px 10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '500', cursor: 'pointer' }}>
                호가조회
              </button>
            </div>
          </div>

          {showAskingPrice && (askingPriceData || realTimeDep) ? (() => {
            const dep = realTimeDep || (askingPriceData?.askingPrice ? parseAskingPriceResponse(askingPriceData, selectedProdId) : null);
            if (!dep) return null;
            const standardPrice = currentPriceData[selectedProdId]?.standardPrice || dep.standardPrice || 0;

            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280' }}>
                    {selectedProdId} {prodNms[selectedProdId] || ''} {
                      isTradingHours() && isRealTimeReceiving ? '(실시간)' : dep.bsopHour ? `(${formatTime(dep.bsopHour)})` : ''
                    }
                  </div>
                  {signalRConnected && isTradingHours() && isRealTimeReceiving && (
                    <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: '500' }}>● 실시간</span>
                  )}
                </div>

                {/* 호가 테이블 */}
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '6px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '33%', fontSize: '11px' }}>매도잔량</th>
                        <th style={{ padding: '6px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '34%', fontSize: '11px' }}>호가</th>
                        <th style={{ padding: '6px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '33%', fontSize: '11px' }}>매수잔량</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* 매도호가 (10행) */}
                      {Array.from({ length: 10 }).map((_, idx) => {
                        const askIdx = 9 - idx;
                        const askPrice = dep.askPrices?.[askIdx];
                        const askQty = dep.askQuantities?.[askIdx];
                        const isStd = isStandardPrice(askPrice, standardPrice);
                        return (
                          <tr key={`ask-${idx}`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '3px 6px', textAlign: 'right', backgroundColor: askPrice ? '#fef2f2' : 'white', color: '#6b7280', fontWeight: askPrice ? '600' : '400', fontSize: '11px', cursor: askPrice ? 'pointer' : 'default' }}
                              onClick={() => askPrice && handleHogaClick(askPrice, true)}
                              onContextMenu={(e) => { if (askPrice) { e.preventDefault(); setPriceContextMenu({ x: e.clientX, y: e.clientY, price: askPrice, isAsk: true }); } }}>
                              {askPrice && askQty ? parseInt(String(askQty)).toLocaleString() : ''}
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', backgroundColor: askPrice ? '#fef2f2' : 'white', color: getPriceColor(askPrice, standardPrice), fontWeight: isStd ? '900' : '600', fontSize: '11px', cursor: askPrice ? 'pointer' : 'default' }}
                              onClick={() => askPrice && handleHogaClick(askPrice, true)}
                              onContextMenu={(e) => { if (askPrice) { e.preventDefault(); setPriceContextMenu({ x: e.clientX, y: e.clientY, price: askPrice, isAsk: true }); } }}>
                              {askPrice ? parseInt(String(askPrice)).toLocaleString() : ''}
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', backgroundColor: 'white', fontSize: '11px' }}></td>
                          </tr>
                        );
                      })}
                      {/* 매수호가 (10행) */}
                      {Array.from({ length: 10 }).map((_, idx) => {
                        const bidPrice = dep.bidPrices?.[idx];
                        const bidQty = dep.bidQuantities?.[idx];
                        const isStd = isStandardPrice(bidPrice, standardPrice);
                        return (
                          <tr key={`bid-${idx}`} style={{ borderBottom: idx < 9 ? '1px solid #e5e7eb' : 'none' }}>
                            <td style={{ padding: '3px 6px', textAlign: 'right', backgroundColor: 'white', fontSize: '11px' }}></td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', backgroundColor: bidPrice ? '#f0fdf4' : 'white', color: getPriceColor(bidPrice, standardPrice), fontWeight: isStd ? '900' : '600', fontSize: '11px', cursor: bidPrice ? 'pointer' : 'default' }}
                              onClick={() => bidPrice && handleHogaClick(bidPrice, false)}
                              onContextMenu={(e) => { if (bidPrice) { e.preventDefault(); setPriceContextMenu({ x: e.clientX, y: e.clientY, price: bidPrice, isAsk: false }); } }}>
                              {bidPrice ? parseInt(String(bidPrice)).toLocaleString() : ''}
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', backgroundColor: bidPrice ? '#f0fdf4' : 'white', color: '#6b7280', fontWeight: bidPrice ? '600' : '400', fontSize: '11px', cursor: bidPrice ? 'pointer' : 'default' }}
                              onClick={() => bidPrice && handleHogaClick(bidPrice, false)}
                              onContextMenu={(e) => { if (bidPrice) { e.preventDefault(); setPriceContextMenu({ x: e.clientX, y: e.clientY, price: bidPrice, isAsk: false }); } }}>
                              {bidPrice && bidQty ? parseInt(String(bidQty)).toLocaleString() : ''}
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ backgroundColor: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                        <td style={{ padding: '4px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '10px' }}>매도: {(dep.totalAskQuantity || 0).toLocaleString()}</td>
                        <td style={{ padding: '4px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '10px' }}></td>
                        <td style={{ padding: '4px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '10px' }}>매수: {(dep.totalBidQuantity || 0).toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* 예상체결가 */}
                {dep.antcCnpr > 0 && (
                  <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '4px' }}>
                    <div style={{ fontSize: '11px', color: '#0369a1', fontWeight: '600' }}>예상체결가</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: getPriceColor(dep.antcCnpr, standardPrice), marginTop: '2px' }}>
                      {dep.antcCnpr.toLocaleString()}
                      <span style={{ fontSize: '11px', fontWeight: '400', marginLeft: '8px', color: dep.antcCntgVrss >= 0 ? '#ef4444' : '#3b82f6' }}>
                        {dep.antcCntgVrss >= 0 ? '+' : ''}{dep.antcCntgVrss.toLocaleString()} ({dep.antcCntgPrdyCtrt >= 0 ? '+' : ''}{dep.antcCntgPrdyCtrt.toFixed(2)}%)
                      </span>
                    </div>
                    {dep.antcVol > 0 && (
                      <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>예상거래량: {dep.antcVol.toLocaleString()}</div>
                    )}
                  </div>
                )}
              </>
            );
          })() : (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>
              호가 정보 대기 중...
            </div>
          )}
        </div>

        {/* 우측: 포트폴리오 주문 폼 */}
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', margin: '0 0 12px 0' }}>포트폴리오 주문</h3>
          <form onSubmit={handleOrderSubmit}>
            {/* 주문 기준일 */}
            <fieldset style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '10px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label htmlFor="order-date" style={{ fontSize: '12px', fontWeight: '500', color: '#1e40af' }}>기준일:</label>
                <DateInput
                  id="order-date"
                  value={formData.date}
                  onChange={(v) => handleInputChange('date', v)}
                  style={{ padding: '4px 8px', border: '1px solid #d1d5db' }}
                />
              </div>
            </fieldset>

            {/* 종목 선택 */}
            <fieldset style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '10px', marginBottom: '10px' }}>
              <div>
                <label htmlFor="order-prod-id" style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#1e40af', marginBottom: '4px' }}>종목코드:</label>
                <input id="order-prod-id" type="text" value={formData.prodId}
                  onChange={(e) => handleInputChange('prodId', e.target.value)}
                  placeholder="종목코드 입력 (예: 069500)"
                  style={{
                    display: 'block', width: '100%', padding: '6px 8px',
                    border: errors.prodId ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box'
                  }}
                />
                {errors.prodId && <div style={{ color: 'red', fontSize: '11px', marginTop: '4px' }}>{errors.prodId}</div>}
                {formData.prodId && formData.prodNm && (
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>{formData.prodNm} ({formData.prodId})</div>
                )}
              </div>
            </fieldset>

            {/* 매수/매도 및 비중/수량 */}
            <fieldset style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '10px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button type="button" onClick={() => handleInputChange('isBuy', true)}
                    style={{ padding: '4px 10px', backgroundColor: formData.isBuy ? '#3b82f6' : '#e5e7eb', color: formData.isBuy ? 'white' : '#374151', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                    매수
                  </button>
                  <button type="button" onClick={() => handleInputChange('isBuy', false)}
                    style={{ padding: '4px 10px', backgroundColor: !formData.isBuy ? '#3b82f6' : '#e5e7eb', color: !formData.isBuy ? 'white' : '#374151', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                    매도
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <label htmlFor="order-wei" style={{ fontSize: '12px', color: '#1e40af' }}>비중(%):</label>
                  <input id="order-wei" type="number" value={formData.wei === 0 ? '' : formData.wei}
                    onChange={(e) => handleInputChange('wei', e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                    onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                    min="0" max="100" step="0.01" placeholder="0"
                    disabled={formData.exitAll || (formData.qty != null && formData.qty > 0)}
                    style={{ width: '70px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <label htmlFor="order-qty" style={{ fontSize: '12px', color: '#1e40af' }}>수량:</label>
                  <input id="order-qty" type="number" value={formData.qty == null ? '' : formData.qty}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseInt(e.target.value) || 0;
                      handleInputChange('qty', value);
                      if (value != null && value > 0) handleInputChange('wei', 0);
                    }}
                    onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                    min="0" step="1" placeholder="0" disabled={formData.exitAll}
                    style={{ width: '70px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                  />
                </div>
              </div>
              {!formData.isBuy && (
                <div style={{ marginTop: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                    <input type="checkbox" checked={formData.exitAll}
                      onChange={(e) => { handleInputChange('exitAll', e.target.checked); if (e.target.checked) handleInputChange('wei', 0); }}
                    />
                    전량 청산
                  </label>
                </div>
              )}
              {errors.wei && <div style={{ color: 'red', fontSize: '11px', marginTop: '4px' }}>{errors.wei}</div>}
            </fieldset>

            {/* 가격 타입 */}
            <fieldset style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '10px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                {(['Opp', 'My', 'Limit', 'Stop'] as const).map(ty => (
                  <button key={ty} type="button" onClick={() => handleInputChange('prcTy', ty)}
                    style={{
                      padding: '4px 10px', backgroundColor: formData.prcTy === ty ? '#3b82f6' : '#e5e7eb',
                      color: formData.prcTy === ty ? 'white' : '#374151', border: 'none', borderRadius: '4px',
                      cursor: 'pointer', fontSize: '12px'
                    }}>
                    {ty === 'Opp' ? '상대호가' : ty === 'My' ? '자기호가' : ty === 'Limit' ? '지정가' : 'STOP'}
                  </button>
                ))}
              </div>

              {formData.prcTy === 'Opp' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-opp-lmt-idx" style={{ fontSize: '12px', color: '#1e40af' }}>상대호가 (1~10):</label>
                  <input id="order-opp-lmt-idx" type="number" value={formData.oppLmtIdx}
                    onChange={(e) => handleInputChange('oppLmtIdx', parseInt(e.target.value) || 0)}
                    min="1" max="10"
                    style={{ width: '60px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                  />
                  {errors.oppLmtIdx && <span style={{ color: 'red', fontSize: '11px' }}>{errors.oppLmtIdx}</span>}
                </div>
              )}
              {formData.prcTy === 'My' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-my-lmt-idx" style={{ fontSize: '12px', color: '#1e40af' }}>자기호가 (1~10):</label>
                  <input id="order-my-lmt-idx" type="number" value={formData.myLmtIdx}
                    onChange={(e) => handleInputChange('myLmtIdx', parseInt(e.target.value) || 0)}
                    min="1" max="10"
                    style={{ width: '60px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                  />
                  {errors.myLmtIdx && <span style={{ color: 'red', fontSize: '11px' }}>{errors.myLmtIdx}</span>}
                </div>
              )}
              {formData.prcTy === 'Limit' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-limit-prc" style={{ fontSize: '12px', color: '#1e40af' }}>지정가:</label>
                  <input id="order-limit-prc" type="number" value={formData.limitPrc || ''}
                    onChange={(e) => handleInputChange('limitPrc', parseFloat(e.target.value) || null)}
                    style={{ width: '120px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                  />
                  {errors.limitPrc && <span style={{ color: 'red', fontSize: '11px' }}>{errors.limitPrc}</span>}
                </div>
              )}
              {formData.prcTy === 'Stop' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-stop-prc" style={{ fontSize: '12px', color: '#1e40af' }}>STOP 가격:</label>
                  <input id="order-stop-prc" type="number" value={formData.stopPrc || ''}
                    onChange={(e) => handleInputChange('stopPrc', parseFloat(e.target.value) || null)}
                    style={{ width: '120px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                  />
                  {errors.stopPrc && <span style={{ color: 'red', fontSize: '11px' }}>{errors.stopPrc}</span>}
                </div>
              )}
            </fieldset>

            {/* 실행 타입 및 시간 */}
            <fieldset style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '10px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                <button type="button" onClick={() => handleInputChange('isSlice', false)}
                  style={{ padding: '4px 10px', backgroundColor: !formData.isSlice ? '#3b82f6' : '#e5e7eb', color: !formData.isSlice ? 'white' : '#374151', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  즉시 실행
                </button>
                <button type="button" onClick={() => {
                    setFormData(prev => ({ ...prev, isSlice: true, hm0: getSliceStartTime(prev.date) }));
                  }}
                  style={{ padding: '4px 10px', backgroundColor: formData.isSlice ? '#3b82f6' : '#e5e7eb', color: formData.isSlice ? 'white' : '#374151', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  시간 분할
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <label htmlFor="order-hm0" style={{ fontSize: '12px', color: '#1e40af' }}>시작:</label>
                <input id="order-hm0" type="text" value={formData.hm0} onChange={(e) => handleInputChange('hm0', e.target.value)}
                  style={{ width: '80px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }} placeholder="09:00" />
              </div>
              {formData.isSlice && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <label htmlFor="order-hm1" style={{ fontSize: '12px', color: '#1e40af' }}>종료:</label>
                    <input id="order-hm1" type="text" value={formData.hm1} onChange={(e) => handleInputChange('hm1', e.target.value)}
                      style={{ width: '80px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }} placeholder="15:20" />
                    {errors.hm1 && <span style={{ color: 'red', fontSize: '11px' }}>{errors.hm1}</span>}
                  </div>
                  {formData.hm1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label htmlFor="order-nSlices" style={{ fontSize: '12px', color: '#1e40af' }}>분할:</label>
                      <input id="order-nSlices" type="number" min={2} max={100} value={formData.nSlices ?? ''}
                        onChange={(e) => handleInputChange('nSlices', e.target.value ? parseInt(e.target.value) : null)}
                        style={{ width: '80px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                        placeholder="자동"
                      />
                    </div>
                  )}
                </>
              )}
            </fieldset>

            {/* 코멘트 */}
            <div style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label htmlFor="order-msg" style={{ fontSize: '12px', color: '#1e40af', whiteSpace: 'nowrap' }}>코멘트:</label>
                <input id="order-msg" type="text" value={formData.msg} onChange={(e) => handleInputChange('msg', e.target.value)}
                  placeholder="주문 메모"
                  style={{ flex: 1, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                />
              </div>
            </div>

            {/* 제출 버튼 — 주문 권한이 없는 계좌면 숨긴다 (서버도 403 으로 막는다) */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {!canOrder && <span style={{ fontSize: '12px', color: '#9ca3af' }}>이 계좌는 조회 전용입니다 — 주문 권한이 없습니다.</span>}
              {canOrder && <button type="submit"
                style={{ padding: '6px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                주문 제출
              </button>}
            </div>
          </form>
        </div>
      </div>

      {/* 주문내역 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', margin: 0 }}>주문내역</h3>
          <button
            onClick={() => fetchOrders(portfolioId, selectedDate, selectedDate)}
            style={{ padding: '3px 10px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', marginLeft: 'auto' }}
          >새로고침</button>
        </div>
        <div className="ag-theme-quartz" style={{ height: 336, width: '100%' }}>
          <AgGridReact {...copyMenu.gridProps}
            rowData={orders}
            columnDefs={orderColumnDefs}
            defaultColDef={defaultColDef}
            animateRows={true}
            pagination={true}
            paginationPageSize={100}
            paginationPageSizeSelector={[10, 25, 50, 100, 500]}
            headerHeight={36}
            rowHeight={30}
            onRowClicked={(event) => {
              const order = event.data as PfOrder;
              if (order.id) {
                fetchSlices(order.id);
              }
            }}
          />
        </div>
      </div>

      {/* 슬라이스 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', margin: 0 }}>
            슬라이스{sliceOrderId ? ` (주문 #${sliceOrderId})` : ''}
          </h3>
        </div>
        <div className="ag-theme-quartz" style={{ height: 336, width: '100%' }}>
          <AgGridReact {...copyMenu.gridProps}
            rowData={slices}
            columnDefs={sliceColumnDefs}
            defaultColDef={defaultColDef}
            animateRows={true}
            headerHeight={36}
            rowHeight={30}
          />
        </div>
      </div>

      {/* 체결내역 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', margin: 0 }}>
            체결내역{fillsFiltered && sliceOrderId ? ` (주문 #${sliceOrderId})` : ''}
          </h3>
          <button
            onClick={() => {
              fetchFills(portfolioId, selectedDate, selectedDate, true);
            }}
            style={{
              padding: '3px 10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px',
              fontSize: '11px', fontWeight: '500', cursor: 'pointer', marginLeft: 'auto',
            }}
          >전체조회</button>
        </div>
        <div className="ag-theme-quartz" style={{ height: 336, width: '100%' }}>
          <AgGridReact {...copyMenu.gridProps}
            rowData={fillsFiltered ? filteredFills : fills}
            columnDefs={fillColumnDefs}
            defaultColDef={defaultColDef}
            animateRows={true}
            pagination={true}
            paginationPageSize={100}
            paginationPageSizeSelector={[10, 25, 50, 100, 500]}
            headerHeight={36}
            rowHeight={30}
          />
        </div>
      </div>

      {/* 포지션 현황 (하단) */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', margin: 0 }}>포지션 현황</h3>
          <button onClick={fetchPositions} disabled={isLoadingPositions}
            style={{
              padding: '5px 12px', backgroundColor: isLoadingPositions ? '#9ca3af' : '#3b82f6',
              color: 'white', border: 'none', borderRadius: '3px', fontSize: '12px', fontWeight: '500',
              cursor: isLoadingPositions ? 'not-allowed' : 'pointer', opacity: isLoadingPositions ? 0.6 : 1
            }}>
            {isLoadingPositions ? '조회 중...' : '조회'}
          </button>
        </div>
        <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>종목코드</th>
                <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600', fontSize: '12px' }}>종목명</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600', fontSize: '12px' }}>보유수량</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600', fontSize: '12px' }}>평균단가</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600', fontSize: '12px' }}>현재가</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600', fontSize: '12px' }}>평가금액</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600', fontSize: '12px' }}>평가손익</th>
              </tr>
            </thead>
            <tbody>
              {positions.length > 0 ? (
                positions.map((pos, idx) => {
                  const prodId = pos.prodId || pos.prodid || '';
                  const prodNm = pos.prodNm || '';
                  const qty = pos.pos || pos.qty || 0;
                  const avgPrc = pos.avgprc || pos.avgPx || 0;
                  const currPrc = pos.c || pos.currPx || 0;
                  const evluAmt = pos.evluamt || (qty * currPrc);
                  const prft = pos.prft || 0;
                  const isSelected = selectedProdId === prodId;

                  return (
                    <tr key={idx}
                      style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: isSelected ? '#eff6ff' : 'white', cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedProdId(prodId);
                        // 호가정보 갱신
                        if (isTradingHours() && isRealTimeReceiving) {
                          const dep = realTimeDeps[prodId];
                          if (dep) { setRealTimeDep(dep); setShowAskingPrice(true); }
                        } else {
                          fetchAskingPriceForProd(prodId);
                        }
                        // 주문폼에 종목 반영
                        setFormData(prev => ({ ...prev, prodId, prodNm }));
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        const currentPrice = currPrc || realTimePrices[prodId]?.currentPrice || 0;
                        setPositionContextMenu({ x: e.clientX, y: e.clientY, prodId, price: currentPrice });
                      }}
                    >
                      <td style={{ padding: '6px', textAlign: 'center', fontSize: '12px', fontWeight: isSelected ? '600' : '400', color: isSelected ? '#1e40af' : '#374151' }}>{prodId}</td>
                      <td style={{ padding: '6px', textAlign: 'left', fontSize: '12px' }}>{prodNm || '-'}</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontSize: '12px' }}>{qty.toLocaleString()}</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontSize: '12px' }}>{avgPrc.toLocaleString()}</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{currPrc.toLocaleString()}</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontSize: '12px' }}>{evluAmt.toLocaleString()}</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: prft >= 0 ? '#ef4444' : '#22c55e' }}>{prft.toLocaleString()}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>포지션이 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 호가 우클릭 메뉴 */}
      {priceContextMenu && (
        <div style={{ position: 'fixed', top: priceContextMenu.y, left: priceContextMenu.x, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1000, minWidth: '120px' }}
          onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '12px', color: '#ef4444', fontWeight: '500', borderBottom: '1px solid #e5e7eb' }}
            onClick={() => {
              setFormData(prev => ({ ...prev, prodId: selectedProdId, prodNm: prodNms[selectedProdId] || '', isBuy: true, prcTy: 'Limit', limitPrc: priceContextMenu.price }));
              setPriceContextMenu(null);
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
            매수 ({priceContextMenu.price.toLocaleString()})
          </div>
          <div style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '12px', color: '#22c55e', fontWeight: '500' }}
            onClick={() => {
              setFormData(prev => ({ ...prev, prodId: selectedProdId, prodNm: prodNms[selectedProdId] || '', isBuy: false, prcTy: 'Limit', limitPrc: priceContextMenu.price }));
              setPriceContextMenu(null);
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
            매도 ({priceContextMenu.price.toLocaleString()})
          </div>
        </div>
      )}

      {/* 포지션 우클릭 메뉴 */}
      {positionContextMenu && (
        <div style={{ position: 'fixed', top: positionContextMenu.y, left: positionContextMenu.x, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1000, minWidth: '120px' }}
          onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '12px', color: '#ef4444', fontWeight: '500', borderBottom: '1px solid #e5e7eb' }}
            onClick={() => {
              const prodId = positionContextMenu.prodId;
              const prodNm = prodNms[prodId] || positions.find(p => (p.prodId || p.prodid) === prodId)?.prodNm || '';
              setFormData(prev => ({ ...prev, prodId, prodNm, isBuy: true, prcTy: 'Opp' }));
              setSelectedProdId(prodId);
              if (!(isTradingHours() && isRealTimeReceiving)) fetchAskingPriceForProd(prodId);
              setPositionContextMenu(null);
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
            매수
          </div>
          <div style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '12px', color: '#22c55e', fontWeight: '500' }}
            onClick={() => {
              const prodId = positionContextMenu.prodId;
              const prodNm = prodNms[prodId] || positions.find(p => (p.prodId || p.prodid) === prodId)?.prodNm || '';
              const position = positions.find(p => (p.prodId || p.prodid) === prodId);
              const qty = position ? (position.pos || position.qty || 0) : 0;
              setFormData(prev => ({ ...prev, prodId, prodNm, isBuy: false, qty, prcTy: 'Opp' }));
              setSelectedProdId(prodId);
              if (!(isTradingHours() && isRealTimeReceiving)) fetchAskingPriceForProd(prodId);
              setPositionContextMenu(null);
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
            매도
          </div>
        </div>
      )}

      {/* 우클릭 메뉴 닫기 (배경 클릭) */}
      {(priceContextMenu || positionContextMenu) && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
          onClick={() => { setPriceContextMenu(null); setPositionContextMenu(null); }}
        />
      )}
      {copyMenu.menu}
    </div>
  );
};

export default Trading;
